export const DOCS_ROOT_PATH = "/docs";

export const DOCS_SITE_TITLE = "Latted Documentation";

export const DOCS_SITE_DESCRIPTION =
  "Complete Latted documentation for creating, editing, automating, and publishing AI video projects with Composer, UGC ads, Studio, academic papers, Brain Explorer, reusable assets, characters, and billing tools.";

export const DOCS_PAGE = {
  id: "latted-user-guide",
  title: "Latted User Guide",
  seoTitle: "Latted Documentation - Complete AI Video Generator and Editor Guide",
  description:
    "Learn how to use Latted's AI video generator and editor for Composer projects, UGC ads, talking-head videos, academic paper explainers, Brain Explorer, reusable assets, characters, automations, billing, and exports.",
  updatedAt: "2026-04-24",
  readingTimeMinutes: 24,
  mdxPublicPath: "/docs/latted-user-guide.mdx",
  keywords: [
    "Latted documentation",
    "AI video generator guide",
    "AI video editor documentation",
    "Latted user guide",
    "UGC ads workflow",
    "talking-head video generator",
    "academic paper video generator",
    "AI video automations",
    "AI video timeline editor",
    "reusable AI video assets",
    "AI video characters",
    "Brain Explorer",
  ],
  sections: [
    { id: "quick-start-workflows", label: "Choose a workflow" },
    { id: "composer-ai-video-generator", label: "Composer projects" },
    { id: "projects-dashboard", label: "Projects dashboard" },
    { id: "editor-timeline", label: "Editor timeline" },
    { id: "ugc-ads", label: "UGC ads" },
    { id: "studio-talking-head", label: "Studio videos" },
    { id: "academic-paper-videos", label: "Academic papers" },
    { id: "brain-explorer", label: "Brain Explorer" },
    { id: "assets-and-characters", label: "Assets & characters" },
    { id: "automations", label: "Automations" },
    { id: "billing-and-account", label: "Billing & account" },
    { id: "export-share-support", label: "Export & support" },
    { id: "troubleshooting", label: "Troubleshooting" },
    { id: "frequently-asked-questions", label: "FAQ" },
  ],
  quickStartCards: [
    {
      title: "Create a general AI video",
      description:
        "Start on the home page with a prompt, attachments, aspect ratio, and model tier. Composer builds an editable project.",
      href: "#composer-ai-video-generator",
    },
    {
      title: "Make UGC ads",
      description:
        "Use the UGC workflow to combine a creator, product media, idea generation, captions, and selected scripts.",
      href: "#ugc-ads",
    },
    {
      title: "Transform talking-head footage",
      description:
        "Upload or record a webcam clip, choose a Studio preset, and turn it into a polished launch video.",
      href: "#studio-talking-head",
    },
    {
      title: "Explain an academic paper",
      description:
        "Upload a PDF, select audience depth and duration, and create a motion-graphics explainer grounded in the paper.",
      href: "#academic-paper-videos",
    },
    {
      title: "Reuse brand context",
      description:
        "Save assets and characters once, then mention or attach them across projects and automations.",
      href: "#assets-and-characters",
    },
    {
      title: "Scale recurring content",
      description:
        "Build automations with saved prompts, settings, schedules, attachments, run history, and output review.",
      href: "#automations",
    },
  ],
  faqItems: [
    {
      question: "What is Latted?",
      answer:
        "Latted is an AI video generation and editing platform that combines prompt-based project creation, reusable assets and characters, a timeline editor, specialized workflows, and automation tools.",
    },
    {
      question: "What is the fastest way to create a Latted video?",
      answer:
        "The fastest path is Composer: write a prompt on the home page, attach helpful references, choose an aspect ratio and model tier, then let the Composer agent build an editable project.",
    },
    {
      question: "Can I edit a video after the AI creates it?",
      answer:
        "Yes. Latted creates editable projects with a preview, storyboard, keyframes, clips, audio tracks, overlays, timing controls, and export tools.",
    },
    {
      question: "Which Latted workflow should I use for ads?",
      answer:
        "Use the UGC workflow when you want creator-style product ads with idea generation, product assets, selected scripts, optional captions, and vertical outputs.",
    },
    {
      question: "Can Latted make videos from PDFs?",
      answer:
        "Yes. The academic paper workflow accepts PDF uploads, validates the file, and lets you choose audience proficiency and target duration before generating a paper-grounded explainer.",
    },
    {
      question: "How do I keep characters and products consistent?",
      answer:
        "Create reusable assets and characters, then attach or mention them when creating projects and automations so recurring visual context is preserved.",
    },
    {
      question: "When should I use support instead of documentation?",
      answer:
        "Use documentation for stable how-to knowledge. Use in-app support when a live project, billing state, or ambiguous next step needs contextual help.",
    },
  ],
};

export function getDocsPage() {
  return DOCS_PAGE;
}

export function buildDocsPath() {
  return DOCS_ROOT_PATH;
}
